import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { formatAuditTime } from '../services/auditTime';
import {
  apiMessage,
  ALL_CREATED_RANGE,
  CREATED_RANGE_OPTIONS,
  createdRangeQuery,
  createdRangeValue,
  createdWindow,
  formatLocalDate,
  getWorkOnTechnicians,
  parseLocalDateParam,
  tenantDisplayName,
  tenantOptionLabel,
} from '../services/jobAuditApi';
import { serviceTitanJobUrl } from './serviceTitanJob';
import { findIssueGroup, technicianIssueGroups } from './technicianIssueGroups';

const FILTER_CLASS =
  'mt-1 block w-40 rounded-md border border-gray-300 bg-white px-2 py-1.5 shadow-sm';

const rangeCaption = (rangeSelect, on) => {
  if (on) return on;
  if (rangeSelect === ALL_CREATED_RANGE) return 'All audits';
  const match = CREATED_RANGE_OPTIONS.find((opt) => opt.value === rangeSelect);
  return match ? match.label : 'Last 7 Days';
};

const formatGapRate = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return `${Number.isInteger(n) ? n : n.toFixed(1)}%`;
};

const rowKey = (row) => `${row.tenant_code || ''}-${row.technician_id || ''}`;

const jobCountLabel = (count) => `${count} ${count === 1 ? 'job' : 'jobs'}`;

const JobTable = ({ jobs, tenantCode, fieldLabel, finding }) => (
  <div className="overflow-x-auto">
    <table className="min-w-full text-left text-xs">
      <thead>
        <tr className="bg-slate-50 text-[11px] font-normal text-gray-500">
          <th className="px-3 py-2.5 font-normal">Job / date</th>
          {fieldLabel ? <th className="px-3 py-2.5 font-normal">Field</th> : null}
          {finding ? <th className="px-3 py-2.5 font-normal">Audit finding</th> : null}
          <th className="px-3 py-2.5 font-normal" />
        </tr>
      </thead>
      <tbody>
        {jobs.map((job) => {
          const jobUrl = serviceTitanJobUrl(tenantCode, job.job_id);
          return (
            <tr key={job.audit_run_id} className="border-t border-slate-200">
              <td className="px-3 py-2.5 align-top">
                <div className="font-semibold text-gray-900">{job.job_id}</div>
                {job.created_at ? (
                  <div className="mt-0.5 text-[11px] text-gray-500">{formatAuditTime(job.created_at)}</div>
                ) : null}
                {jobUrl ? (
                  <a
                    href={jobUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-700 hover:underline"
                  >
                    ServiceTitan
                  </a>
                ) : null}
              </td>
              {fieldLabel ? <td className="px-3 py-2.5 align-top text-gray-700">{fieldLabel}</td> : null}
              {finding ? <td className="px-3 py-2.5 align-top text-gray-700">{finding}</td> : null}
              <td className="px-3 py-2.5 align-top text-right">
                <Link to={`/audit/${job.audit_run_id}`} className="text-blue-700 underline">
                  View audit
                </Link>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

const IssueRow = ({ group, omitField, open, onToggle, tenantCode }) => (
  <div className="my-2 overflow-hidden rounded-md border border-slate-200 bg-white">
    <button
      type="button"
      aria-expanded={open}
      onClick={onToggle}
      className="grid w-full grid-cols-[1fr_auto] items-center gap-3 px-3 py-3 text-left text-[13px] hover:bg-slate-50"
    >
      <span className="min-w-0">
        <span className="block font-semibold text-gray-900">{group.reasonLabel || group.label}</span>
        {!omitField && group.fieldLabel ? (
          <span className="mt-0.5 block text-[11px] text-gray-500">{group.fieldLabel}</span>
        ) : null}
      </span>
      <span className="text-xs text-blue-700">{jobCountLabel(group.count)} ▾</span>
    </button>
    {open ? (
      <div className="border-t border-slate-200">
        <JobTable
          jobs={group.jobs}
          tenantCode={tenantCode}
          fieldLabel={group.fieldLabel}
          finding={group.reasonLabel || group.label}
        />
      </div>
    ) : null}
  </div>
);

const TechnicianList = ({ data }) => {
  const omitField = Boolean(data.field_id);
  const rows = data.technicians || [];
  const [openTechs, setOpenTechs] = useState(() =>
    rows[0] ? new Set([rowKey(rows[0])]) : new Set()
  );
  const [openIssues, setOpenIssues] = useState(() => new Set());

  const toggleTech = (key) => {
    setOpenTechs((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleIssue = (key) => {
    setOpenIssues((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  return (
    <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
      <div className="hidden grid-cols-[1.1fr_0.7fr_0.55fr_1.2fr_9.5rem] gap-4 bg-slate-100 px-4 py-3 text-[11px] uppercase tracking-wide text-gray-500 md:grid">
        <span>Technician</span>
        <span>Audits with a gap</span>
        <span>Gap rate</span>
        <span>Recurring issue</span>
        <span>Details</span>
      </div>
      {rows.map((row) => {
        const key = rowKey(row);
        const open = openTechs.has(key);
        const groups = technicianIssueGroups(row.jobs, { omitField });
        const top = (groups || []).find((group) => group.top) || null;
        const place = tenantDisplayName(row.tenant_code, data.tenants);
        const auditsHref = row.technician_id
          ? `/audit/technicians/${encodeURIComponent(row.technician_id)}?${new URLSearchParams({
              ...(row.tenant_code ? { tenant: row.tenant_code } : {}),
              ...(row.technician ? { name: row.technician } : {}),
            }).toString()}`
          : '';
        return (
          <div key={key} className="border-t border-slate-200">
            <div className="grid grid-cols-2 items-center gap-x-4 gap-y-2 px-4 py-4 text-[13px] md:grid-cols-[1.1fr_0.7fr_0.55fr_1.2fr_9.5rem]">
              <div>
                {auditsHref ? (
                  <Link to={auditsHref} className="font-semibold text-gray-900 hover:underline">
                    {row.technician}
                  </Link>
                ) : (
                  <div className="font-semibold text-gray-900">{row.technician}</div>
                )}
                {place ? <div className="mt-1 text-[11px] text-gray-500">{place}</div> : null}
              </div>
              <div>
                <div className="font-semibold tabular-nums text-gray-900">
                  {row.failed} of {row.applicable}
                </div>
                <div className="mt-1 text-[11px] text-gray-500">Applicable audits</div>
              </div>
              <div className="text-base font-bold tabular-nums text-gray-900">
                {formatGapRate(row.fail_pct)}
              </div>
              <div>
                {top ? (
                  <>
                    <span className="inline-block rounded bg-amber-100 px-1.5 py-1 text-[11px] text-amber-900">
                      {top.label}
                    </span>
                    <div className="mt-1 text-[11px] text-gray-500">Top issue</div>
                  </>
                ) : (
                  <span className="text-gray-400">—</span>
                )}
              </div>
              <button
                type="button"
                aria-expanded={open}
                onClick={() => toggleTech(key)}
                className={`col-span-2 justify-self-start rounded-md border border-slate-200 px-2.5 py-1.5 text-xs text-blue-700 md:col-span-1 md:justify-self-stretch ${
                  open ? 'bg-blue-50' : 'bg-white'
                }`}
              >
                {open ? 'Hide breakdown' : 'View breakdown'}
              </button>
            </div>
            {open ? (
              <div className="border-t border-slate-200 bg-slate-50 px-4 pb-4">
                <h2 className="py-4 text-[15px] font-semibold text-gray-900">
                  {row.technician} · issue breakdown
                </h2>
                {groups ? (
                  <>
                    <p className="mb-3 rounded-md bg-blue-50 px-3 py-2.5 text-xs text-gray-600">
                      {row.failed} unique audits have a gap. Group counts can overlap and should
                      not be added together.
                    </p>
                    {groups.map((group) => (
                      <IssueRow
                        key={group.key}
                        group={group}
                        omitField={omitField}
                        tenantCode={row.tenant_code}
                        open={openIssues.has(`${key}:${group.key}`)}
                        onToggle={() => toggleIssue(`${key}:${group.key}`)}
                      />
                    ))}
                  </>
                ) : (
                  <div className="overflow-hidden rounded-md border border-slate-200 bg-white">
                    <JobTable jobs={row.jobs || []} tenantCode={row.tenant_code} />
                  </div>
                )}
              </div>
            ) : null}
          </div>
        );
      })}
      {rows.length === 0 && (
        <div className="border-t border-slate-200 px-4 py-10 text-center text-sm text-gray-500">
          No technicians failed this in the selected window.
        </div>
      )}
    </div>
  );
};

const EMPTY = {
  item_id: '',
  issue_label: '',
  field_id: '',
  field_label: '',
  technicians: [],
  tenants: [],
};

const JobAuditWorkOnTechnicians = () => {
  const { itemId, technicianId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const tenant = searchParams.get('tenant') || '';
  const techTenant = searchParams.get('tech_tenant') || '';
  const field = searchParams.get('field') || '';
  const reason = searchParams.get('reason') || '';
  const issueField = searchParams.get('issue_field') || '';
  const otherIssue = searchParams.get('other') === '1';
  const rangeParam = searchParams.get('range') || '';
  const on = searchParams.get('on') || '';
  const rangeSelect = createdRangeValue(rangeParam, on);
  const range = createdRangeQuery(rangeParam, on);
  const onDate = parseLocalDateParam(on);

  const [loading, setLoading] = useState(true);
  const [missing, setMissing] = useState(false);
  const [data, setData] = useState(EMPTY);

  const setFilter = useCallback(
    (patch) => {
      const next = new URLSearchParams(searchParams);
      Object.entries(patch).forEach(([key, value]) => {
        if (value) next.set(key, value);
        else next.delete(key);
      });
      setSearchParams(next);
    },
    [searchParams, setSearchParams]
  );

  const categoryHref = (() => {
    const params = new URLSearchParams();
    if (tenant) params.set('tenant', tenant);
    if (on) params.set('on', on);
    else if (range) params.set('range', range);
    else if (rangeSelect === ALL_CREATED_RANGE) params.set('range', ALL_CREATED_RANGE);
    const qs = params.toString();
    const path = `/audit/summary/${encodeURIComponent(itemId)}`;
    return qs ? `${path}?${qs}` : path;
  })();

  const windowParams = () => {
    const params = new URLSearchParams();
    if (tenant) params.set('tenant', tenant);
    if (field) params.set('field', field);
    if (on) params.set('on', on);
    else if (range) params.set('range', range);
    else if (rangeSelect === ALL_CREATED_RANGE) params.set('range', ALL_CREATED_RANGE);
    return params;
  };

  const techniciansHref = (() => {
    const qs = windowParams().toString();
    const path = `/audit/summary/${encodeURIComponent(itemId)}/technicians`;
    return qs ? `${path}?${qs}` : path;
  })();

  const summaryHref = (() => {
    const params = new URLSearchParams();
    if (tenant) params.set('tenant', tenant);
    if (on) params.set('on', on);
    else if (range) params.set('range', range);
    else if (rangeSelect === ALL_CREATED_RANGE) params.set('range', ALL_CREATED_RANGE);
    const qs = params.toString();
    return qs ? `/audit/summary?${qs}` : '/audit/summary';
  })();

  useEffect(() => {
    let cancelled = false;
    const bounds = createdWindow({ range, on });
    setLoading(true);
    setMissing(false);
    getWorkOnTechnicians(itemId, {
      tenant: technicianId ? techTenant || tenant : tenant,
      field,
      created_after: bounds.created_after || '',
      created_before: bounds.created_before || '',
    })
      .then((payload) => {
        if (!cancelled) setData(payload);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err?.response?.status === 404) {
          setMissing(true);
          setData(EMPTY);
          return;
        }
        toast.error(apiMessage(err, 'Failed to load technicians for this problem'));
        setData(EMPTY);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [itemId, technicianId, tenant, techTenant, field, range, on]);

  const title = data.field_label || data.issue_label || 'Technicians';
  const omitField = Boolean(data.field_id);
  const technician = technicianId
    ? (data.technicians || []).find(
        (row) =>
          row.technician_id === technicianId &&
          (!techTenant || row.tenant_code === techTenant)
      )
    : null;
  const issueGroup = technician
    ? findIssueGroup(technicianIssueGroups(technician.jobs, { omitField }), {
        other: otherIssue,
        reason,
        issueField,
        omitField,
      })
    : null;
  const place = !tenant
    ? 'All centers'
    : tenantDisplayName(tenant, data.tenants) === tenant
      ? tenant
      : `${tenantDisplayName(tenant, data.tenants)} · ${tenant}`;

  if (technicianId) {
    return (
      <div className="space-y-4">
        <div>
          <Link to={techniciansHref} className="text-sm text-blue-700 hover:underline">
            ← {title}
          </Link>
          <h1 className="mt-2 text-2xl font-semibold text-gray-900">
            {issueGroup?.label || 'Issue'}
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            {technician?.technician || 'Technician'}
            {technician?.tenant_code ? ` · ${technician.tenant_code}` : ''}
            {issueGroup
              ? ` · ${issueGroup.count} ${issueGroup.count === 1 ? 'job' : 'jobs'}`
              : ''}
            {issueGroup?.top ? ' · Top issue' : ''}
          </p>
        </div>
        {loading ? (
          <div className="flex flex-col items-center justify-center gap-3 rounded-lg bg-white py-16 shadow">
            <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600" />
            <p className="text-sm text-gray-600">Loading jobs…</p>
          </div>
        ) : missing ? (
          <div className="rounded-lg bg-white p-6 text-sm text-gray-600 shadow">
            This checklist item is not in the score policy.
          </div>
        ) : !issueGroup ? (
          <div className="rounded-lg bg-white p-6 text-sm text-gray-600 shadow">
            This issue is not in the selected window.
          </div>
        ) : (
          <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
            <JobTable
              jobs={issueGroup.jobs}
              tenantCode={technician.tenant_code}
              fieldLabel={issueGroup.fieldLabel}
              finding={issueGroup.reasonLabel || issueGroup.label}
            />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <div className="text-xs text-gray-500">
          <Link to={summaryHref} className="hover:underline">
            HHRC
          </Link>
          {' / '}
          {place}
          {data.issue_label ? (
            <>
              {' / '}
              <Link to={categoryHref} className="hover:underline">
                {data.issue_label}
              </Link>
            </>
          ) : null}
        </div>
        <h1 className="mt-2 text-[22px] font-semibold text-gray-900">{title}</h1>
        <p className="mt-1 text-[13px] text-gray-500">
          Identify the recurring issue, then open the affected jobs.
        </p>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3 text-xs text-gray-500">
        <span>
          <span className="font-semibold text-gray-800">{place}</span>
          {'  '}
          {rangeCaption(rangeSelect, on)}
        </span>
        {data.issue_label ? <span>Category: {data.issue_label}</span> : null}
      </div>

      <div className="flex flex-wrap items-end gap-3 rounded-lg bg-white p-4 shadow">
        <label className="text-sm text-gray-700">
          Tenant
          <select
            className="mt-1 block w-56 rounded-md border border-gray-300 bg-white px-2 py-1.5 shadow-sm"
            value={tenant}
            onChange={(e) => setFilter({ tenant: e.target.value })}
          >
            <option value="">all</option>
            {(data.tenants || []).map((option) => (
              <option key={option.code || option} value={option.code || option}>
                {tenantOptionLabel(option)}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm text-gray-700">
          Created
          <select
            className={FILTER_CLASS}
            value={rangeSelect}
            onChange={(e) => setFilter({ range: e.target.value, on: '' })}
          >
            <option value={ALL_CREATED_RANGE}>all</option>
            {CREATED_RANGE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm text-gray-700">
          Date
          <DatePicker
            selected={onDate}
            onChange={(date) => setFilter({ on: date ? formatLocalDate(date) : '', range: '' })}
            dateFormat="yyyy-MM-dd"
            isClearable
            placeholderText="pick a day"
            wrapperClassName="mt-1 block"
            className="block w-40 rounded-md border border-gray-300 bg-white px-2 py-1.5 shadow-sm"
          />
        </label>
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center gap-3 rounded-lg bg-white py-16 shadow">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600" />
          <p className="text-sm text-gray-600">Loading technicians…</p>
        </div>
      ) : missing ? (
        <div className="rounded-lg bg-white p-6 text-sm text-gray-600 shadow">
          This checklist item is not in the score policy.
        </div>
      ) : (
        <TechnicianList data={data} />
      )}
    </div>
  );
};

export default JobAuditWorkOnTechnicians;
